import { Injectable } from '@angular/core';
import { AgentService } from '../core/agent/agent.service';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { timeZones } from '../configuration/configuration';
import { ConfigurationHelperService } from '../configuration/configuration-helper.service';
import { UserService } from '../core/user/user.service';
import { CallFinishedNotificationChannel, Configuration, CustomQuestionType, TrainingFaq, BackgroundNoiseType, TestCallQuestionsStatus, TrainingStatus } from '../core/agent/agent';
import { catchError, concat, debounceTime, filter, finalize, forkJoin, interval, Observable, of, ReplaySubject, Subject, switchMap, take, takeUntil, takeWhile, tap } from 'rxjs';
import { Business, BusinessPhoneNumberType, NotificationChannelType, OnboardingIntent, Service } from '../core/business/business';
import { QueryParamsHandling, Router } from '@angular/router';
import { ProgressStatus } from '../shared/agent-setup-process/agent-setup-process.component';
import { BusinessService } from '../core/business/business.service';
import { areEqual } from '@rallycommerce/common/utils.js';
import { AnalyticsEvent, InternalAnalyticsService } from '../core/internal-analytics.service';
import { formatPhoneNumber, getFaviconUrl } from '../core/helpers/utils';
import { ConversationType } from '../core/conversations/conversation';
import { SessionService } from '../core/session.service';
import { SubscriptionService } from '../core/subscription/subscription.service';
import { GooglePlacesSearchService } from '../shared/google-places-search/google-places-search.service';
import { isRedesignedOnboardingVariant } from '../core/helpers/signup-variant';
import { WEBSITE_URL_PATTERN } from '../shared/validators/website-url.validator';

// ENG-2333: the two business-specific test call questions are stored a few
// seconds after training finishes (prod: p50 3 s, 97% within 8 s). The loader's
// last step stays open until they are final, so the "ready for a test call"
// screen shows them at once. Capped at 8 s; with no status (no questions on
// the way, or the API has not written "pending" yet) it checks once more.
const TEST_CALL_QUESTIONS_POLL_MS = 1000;
const TEST_CALL_QUESTIONS_MAX_POLLS = 8;

@Injectable({
  providedIn: 'root'
})
export class GuidedSetupService {
  private isTrainingInProgressInitialized = false;
  private isProgressFinalizeScheduled = false;
  // Set once the loader stopped waiting for the questions: the ready screen
  // then shows the static list instead of a skeleton when they are late.
  hasWaitedForTestCallQuestions = false;
  initialSetupFormValue: any;
  redirectInProgress: boolean = false;
  progressSubject = new Subject<ProgressStatus>();
  sourceEditModeEnabled: boolean = false;
  activeStep: GuidedSetupStepType = GuidedSetupStepType.Train;
  setupForm: FormGroup;
  services: string[] = [];
  // Full Google place details captured when the user picks a business in the
  // places search (ENG-2201). Lets the training/test screens render the business
  // card (photo, name, address) immediately, before the scrape persists anything.
  selectedPlaceDetails: any = null;
  // The redesigned ENG-2201 flow (immersive screens, test call right after
  // training) ships to Variant C only; everyone else keeps the original flow.
  get isRedesignedFlow(): boolean {
    return isRedesignedOnboardingVariant(this.user.userData?.signupVariant);
  }

  // The unified Train → Customize → Go Live flow (ENG-2292). Resolved by the
  // API per business; every legacy path stays untouched when this is false.
  get isUnifiedFlow(): boolean {
    return this.user.isUnifiedOnboardingFlow;
  }

  // Completion flags are stamped positionally from this order (see
  // getCompletionConfigForStep), so the array order IS the flow order. The
  // order is the same for every variant: Train → Customize → (Test on trial
  // billing) → Go Live. Variant C's "give her a call" screen is NOT the Test
  // step — it's the Train step's completion state (ENG-2201, Jess); the
  // original Test step stays after Customize (copy updates land via ENG-2202).
  get guidedSetupSteps(): GuidedSetupStep[] {
    if (this.isUnifiedFlow) {
      // ENG-2292: one three-step flow for every billing version. The Test
      // step is folded into Train's completion state.
      return [
        { number: 1, label: 'Train', key: GuidedSetupStepType.Train },
        { number: 2, label: 'Customize', key: GuidedSetupStepType.Customize },
        { number: 3, label: 'Go Live', key: GuidedSetupStepType.Launch, completionFlag: 'launchStepViewed' },
      ];
    }
    const steps: GuidedSetupStep[] = [
      { number: 1, label: 'Train', key: GuidedSetupStepType.Train },
      { number: 2, label: 'Customize', key: GuidedSetupStepType.Customize },
    ];
    if (this.user.isUserOnTrialBasedBilling) {
      steps.push({ number: 3, label: 'Test', key: GuidedSetupStepType.Test });
    }
    steps.push({ number: steps.length + 1, label: 'Go Live', key: GuidedSetupStepType.Launch, completionFlag: 'launchStepViewed' });
    return steps;
  }
  constructor(public agent: AgentService, private config: ConfigurationHelperService, private fb: FormBuilder, private user: UserService, private router: Router, private businessService: BusinessService, private analytics: InternalAnalyticsService, private session: SessionService, private subscriptionService: SubscriptionService, private googlePlaces: GooglePlacesSearchService) {
    this.session.refreshState.subscribe(() => {
      if (this.setupForm && this.agent.primary && !this.isInitialTraining && !this.session.isAwaitingInitialSetup) {
        this.refreshFormData();
      }
    });

    this.subscriptionService.websiteTextingPreSelected$.subscribe((value: boolean) => {
      if (this.setupForm?.get('websiteTextingPreSelected')) {
        this.setupForm.get('websiteTextingPreSelected').setValue(value, { emitEvent: false });
      }
      this.updateAgentConfig({ websiteTextingPreSelected: value }, true);
    });
  }

  get hasGoogleBusinessProfile(): boolean {
    return !!this.agent.businessInformation?.additionalProperties?.placeId;
  }

  get hasBusinessUrl(): boolean {
    return !!this.agent.businessInformation?.additionalProperties?.url;
  }

  // A business has training sources when the pre-account flow scraped a GBP or
  // website into it. The direct-signup variants (C/D) have neither (empty business),
  // so they land on the Train step in input mode instead of auto-completing to
  // Customize.
  get hasTrainingSources(): boolean {
    return this.hasGoogleBusinessProfile || this.hasBusinessUrl;
  }

  get userEmail(): string {
    return this.user.email;
  }

  get business(): Business {
    return this.agent.business;
  }

  get isTrainingInProgress(): boolean {
    return this.agent.isTrainingInProgress;
  }

  get isSourceEditModeEnabled() {
    return this.sourceEditModeEnabled;
  }

  get isNumberForwardingEnabled(): boolean {
    return this.agent.primary?.configuration?.numberForwardingEnabled;
  }

  get agentPhoneNumber(): string {
    return this.agent.phoneNumber;
  }

  get lastActiveConfigurationStep(): string {
    return this.agent.primary?.configuration?.lastActiveConfigurationStep;
  }

  get isTrainStepCompleted(): boolean {
    return this.agent.isTrainStepCompleted;
  }

  get isCustomizeStepCompleted(): boolean {
    return this.agent.isCustomizeStepCompleted;
  }

  get isTestStepCompleted(): boolean {
    return this.agent.isTestStepCompleted;
  }

  get isLaunchStepViewed(): boolean {
    return this.agent.isLaunchStepViewed;
  }

  get isSetupCompleted(): boolean {
    return this.isLaunchStepViewed || this.agent.primary?.configuration?.talkToRosieCompleted || this.agent.primary?.configuration?.initialSetupCompleted;
  }

  private getCustomGreetingMessage(): string {
    const hasCustomGreetingEnabled = this.agent.primary?.configuration?.hasCustomGreetingEnabled;
    const customGreeting = this.agent.primary?.configuration?.customGreeting;
    const hasLegalDisclaimerEnabled = this.agent.primary?.configuration?.hasLegalDisclaimerEnabled !== undefined ? this.agent.primary?.configuration?.hasLegalDisclaimerEnabled : true;

    if (hasCustomGreetingEnabled && customGreeting) {
      return !hasLegalDisclaimerEnabled ? customGreeting.replace(':legal_disclaimer', '') : customGreeting;
    }

    return this.agent.primary?.greeting?.default || '';
  }

  get isRedirectInProgress(): boolean {
    return this.redirectInProgress;
  }

  get isInitialTraining(): boolean {
    const business = this.agent.business;
    return !business?.information?.additionalProperties;
  }

  get isGuidedSetupCompleted(): boolean {
    const isMultiBusinessAccount = this.user.userData?.hasMultipleBusinesses;
    const isSetupCompleted = this.isSetupCompleted;
    const isLegacyAccount = this.user.userData?.createdAt ?
      new Date(this.user.userData.createdAt) < new Date('2025-06-17') : false;

    return isSetupCompleted || (isMultiBusinessAccount && !this.user.isUserOnTrialBasedBilling && !this.user.isUserOnFreeMinutesBillingVersion) || isLegacyAccount;
  }

  get isSetupFormInitialized(): boolean {
    return !!this.setupForm;
  }

  // Variant C only: true while the user is inside the redesigned guided setup
  // on any step before Go Live — the immersive part of the flow where global
  // chrome (sidebar, trial banner) stays hidden. Keyed off the current step —
  // navigating back to an earlier step hides the chrome again — not a
  // completion-flag latch, which flips the moment the user *arrives* on a step.
  get isOnPreLaunchSetupStep(): boolean {
    return (this.isRedesignedFlow || this.isUnifiedFlow)
      && this.router.url.includes('/setup')
      && this.activeStep !== GuidedSetupStepType.Launch
      && !this.isGuidedSetupCompleted;
  }

  // Guided setup runs nav-less through Train and Customize for EVERY variant —
  // regardless of which flow (redesigned C or original) renders the steps. The
  // nav returns at the test-call step (ENG-2207 expects the sidebar there) and
  // stays for Go Live. Shared by the dashboard (desktop sidebar) and
  // nav-sidebar (mobile burger) so both gate identically. Completed accounts
  // revisiting setup keep the nav.
  get shouldHideNavForGuidedSetup(): boolean {
    const isBeforeTestStep = this.activeStep === GuidedSetupStepType.Train
      || this.activeStep === GuidedSetupStepType.Customize;
    return this.router.url.includes('/setup')
      && isBeforeTestStep
      && !this.isGuidedSetupCompleted;
  }

  // The user has reached the test-call step (or beyond): the sidebar's usage
  // box and feature cards become relevant from here (ENG-2207).
  get hasReachedTestCallStep(): boolean {
    // The unified flow has no Test step: the chrome returns at Go Live.
    if (this.isUnifiedFlow && this.activeStep === GuidedSetupStepType.Launch) {
      return true;
    }
    return this.activeStep === GuidedSetupStepType.Test
      || this.isTestStepCompleted
      || this.isLaunchStepViewed
      || this.isGuidedSetupCompleted;
  }

  addCustomQuestion() {
    const formArray = this.setupForm.get('questions') as FormArray;
    formArray.push(this.config.createCustomQuestion({ question: '' }, true));
  }

  removeCustomQuestion(index: number) {
    const formArray = this.setupForm.get('questions') as FormArray;
    formArray.removeAt(index);
  }

  addFaq() {
    const formArray = this.setupForm.get('faqs') as FormArray;
    formArray.push(this.config.createFaq({ question: '', answer: '' }));
  }

  removeFaq(index: number) {
    const formArray = this.setupForm.get('faqs') as FormArray;
    formArray.removeAt(index);
  }

  retrainRosie(source?: 'google' | 'website'): Observable<any> {
    const setupData = this.setupForm.value;
    const placeId = setupData?.placeId;
    const url = this.setupForm?.get('url')?.invalid ? null : setupData?.url;
    if (!url) {
      this.setupForm?.get('url').patchValue('');
    }
    this.agent.primary.trainingStatus = TrainingStatus.InProgress;
    const event = this.isInitialTraining ? AnalyticsEvent.SourcesInitialTrainAction : AnalyticsEvent.SourcesRetrainAction;
    this.analytics.trackEvent(event, { source });
    return this.agent.retrainAgent(url, placeId);
  }

  goToStep(step: GuidedSetupStepType, queryParams?: any, queryParamsHandling: QueryParamsHandling = ''): Promise<void> {
    this.redirectInProgress = true;
    return this.router.navigate(['/setup', { step }], { queryParams, queryParamsHandling: queryParamsHandling })
      .then(() => {
        if (step === GuidedSetupStepType.Launch) {
          this.analytics.fireLaunchPageViewed();
        }
        this.updateAgentConfig(this.getCompletionConfigForStep(step));
      }).finally(() => {
        this.redirectInProgress = false;
        if (this.session.isMobileVersion) {
          document.querySelector('.guided-setup-wrapper')?.scrollIntoView({ behavior: 'instant', block: 'start' });
        }
      });
  }

  updateAgentConfig(configuration: Configuration, omitAgentStateRefresh: boolean = false) {
    this.agent.updateAgentConfiguration({ configuration }, omitAgentStateRefresh)
      .subscribe(() => {
        if (!omitAgentStateRefresh) {
          this.agent.getAgent().subscribe();
        }
      });
  }

  // Returns completion flags for all steps up to and including the target step.
  // Convention: flag name is `${stepKey}StepCompleted`. Override via completionFlag if different.
  private getCompletionConfigForStep(step: GuidedSetupStepType): Partial<Configuration> {
    const config: Partial<Configuration> = {};
    for (const s of this.guidedSetupSteps) {
      const flag = s.completionFlag || `${s.key}StepCompleted`;
      (config as any)[flag] = true;
      if (s.key === step) break;
    }
    return config;
  }

  handleTrainingInProgress() {
    if (!this.isTrainingInProgressInitialized) {
      this.isTrainingInProgressInitialized = true;
      this.progressSubject.pipe(filter((status) => status === ProgressStatus.Start), debounceTime(1000)).subscribe(() => this.handleAgentRefresh())
    }
    if (this.isTrainingInProgress) {
      this.progressSubject.next(ProgressStatus.Start);
    }
  }

  private handleAgentRefresh() {
    this.isProgressFinalizeScheduled = false;
    const refreshAgent = concat(
      this.agent.getAgent().pipe(
        tap((agent) => {
          if (!this.agent.isTrainingInProgress) {
            this.finalizeAgentRefresh();
          }
        }),
        takeWhile(agent => agent.trainingStatus === TrainingStatus.InProgress)
      ),
      interval(2000).pipe(
        switchMap(() => this.agent.getAgent().pipe(
          tap(agent => {
            if (!this.agent.isTrainingInProgress) {
              this.finalizeAgentRefresh();
            }
          })
        )),
        takeWhile(agent => agent.trainingStatus === TrainingStatus.InProgress)
      )
    );
    // Root singleton: stop the poll at logout so it cannot outlive the session.
    refreshAgent.pipe(takeUntil(this.session.terminate)).subscribe();
  }

  handlePhoneNumberCreation() {
    if (!this.agentPhoneNumber) {
      const refreshAgent = concat(
        this.agent.getAgent().pipe(takeWhile(agent => agent.phoneNumbers?.length === 0)),
        interval(2000).pipe(
          switchMap(() => this.agent.getAgent()),
          takeWhile(agent => agent.phoneNumbers?.length === 0)
        )
      );
      // Root singleton: stop the poll at logout so it cannot outlive the session.
      refreshAgent.pipe(takeUntil(this.session.terminate)).subscribe();
    }
  }

  restoreSourceInformation() {
    const business = this.agent.business;
    const businessInformation = this.agent.businessInformation?.additionalProperties || {};
    const value = {
      name: businessInformation?.name || business?.title,
      url: businessInformation.url,
      placeId: businessInformation.placeId,
    };
    this.setupForm.patchValue(value);
  }

  // --- Business card data (ENG-2201) ---------------------------------------
  // The training/test screens render from what's available *right now*: the
  // place details captured at selection (photo, formatted address) or the typed
  // URL, then fill in from the scraped business information as it lands.

  // Google-sourced training shows the address first (like the place listing);
  // website-sourced shows the website first.
  get isGoogleTrainingSource(): boolean {
    return !!(this.setupForm?.get('placeId')?.value || this.agent.businessInformation?.additionalProperties?.placeId);
  }

  get trainingCardName(): string {
    const businessInformation = this.agent.businessInformation?.additionalProperties;
    return this.selectedPlaceDetails?.name || businessInformation?.name || this.setupForm?.get('name')?.value || this.business?.title || '';
  }

  // Name from actual sources only (place selection or scrape) — no fallback to
  // the default business title. While training, an empty value here means the
  // card shows a shimmer instead of "Your Business" (ENG-2201 QA).
  get trainingCardScrapedName(): string {
    return this.selectedPlaceDetails?.name || this.agent.businessInformation?.additionalProperties?.name || '';
  }

  get trainingCardAddress(): string {
    if (this.selectedPlaceDetails?.formattedAddress) {
      return this.selectedPlaceDetails.formattedAddress;
    }
    const address = this.agent.businessInformation?.additionalProperties?.addresses?.[0];
    if (!address) {
      return '';
    }
    return [address.street, address.city, [address.state, address.zipCode].filter(Boolean).join(' ')].filter(Boolean).join(', ');
  }

  get trainingCardWebsite(): string {
    return this.setupForm?.get('url')?.value || this.agent.businessInformation?.additionalProperties?.url || '';
  }

  // Scraped business summary for the tile (ENG-2292). Empty until the scrape
  // persists one, so the training card shows a shimmer row meanwhile.
  get trainingCardSummary(): string {
    return this.agent.businessInformation?.additionalProperties?.summary || '';
  }

  get trainingCardImageUrl(): string {
    // Primary source is the API-persisted image (rehosted place photo or site
    // favicon), which lands within one poll of the place fetch. The place
    // details photo bridges the gap the instant a place is selected — its URL
    // carries the (URL-restricted) Google key, which is acceptable — and the
    // favicon covers website training and pre-existing businesses.
    const persistedImageUrl = this.agent.businessInformation?.additionalProperties?.imageUrl;
    return persistedImageUrl
      || this.selectedPlaceDetails?.iconUrl
      || getFaviconUrl(this.trainingCardWebsite);
  }

  // Re-hydrates the selected place details (name, formatted address) after a
  // reload mid-flow: the details are only held in memory, but the place id is
  // persisted on the business, so we can fetch them again for the business
  // card. The card image is NOT taken from here — the response's iconUrl
  // embeds the Google API key; the image comes from the API-persisted
  // additionalProperties.imageUrl instead.
  ensurePlaceDetails(): void {
    const placeId = this.setupForm?.get('placeId')?.value || this.agent.businessInformation?.additionalProperties?.placeId;
    if (!placeId || this.selectedPlaceDetails?.placeId === placeId) {
      return;
    }
    this.googlePlaces.getPlaceDetails(placeId).subscribe({
      next: (place) => this.selectedPlaceDetails = place,
      error: () => { }
    });
  }

  private finalizeAgentRefresh() {
    this.resetSetupForm();
    this.sourceEditModeEnabled = false;
    // Tracked when training really finishes, so the training time does not
    // include the wait for the test call questions below.
    this.analytics.trackEvent(AnalyticsEvent.SourcesTrainActionCompleted);
    // this.analytics.fireInitialBusinessScrapeFinished();
    this.finalizeProgressWhenTestCallQuestionsAreFinal();
  }

  private finalizeProgressWhenTestCallQuestionsAreFinal(): void {
    if (this.isProgressFinalizeScheduled) {
      return;
    }
    this.isProgressFinalizeScheduled = true;
    if (!this.shouldWaitForTestCallQuestions) {
      this.progressSubject.next(ProgressStatus.Finalize);
      return;
    }
    interval(TEST_CALL_QUESTIONS_POLL_MS).pipe(
      take(TEST_CALL_QUESTIONS_MAX_POLLS),
      switchMap(() => this.agent.getAgent()),
      takeWhile(() => this.agent.isTestCallQuestionsPending),
      finalize(() => {
        this.hasWaitedForTestCallQuestions = true;
        this.progressSubject.next(ProgressStatus.Finalize);
      }),
      takeUntil(this.session.terminate)
    ).subscribe({ error: () => { } });
  }

  // Only the redesigned Train step shows the questions on its ready screen;
  // Shopify sign-ups keep the static list.
  private get shouldWaitForTestCallQuestions(): boolean {
    if (!this.isRedesignedFlow && !this.isUnifiedFlow) {
      return false;
    }
    if (this.user.hasCommerceApp) {
      return false;
    }
    const status = this.agent.configuration?.testCallQuestions?.status;
    return status !== TestCallQuestionsStatus.Ready && status !== TestCallQuestionsStatus.Static;
  }

  initializeSetupForm() {
    const business = this.agent.business;

    const businessInformation = this.agent.businessInformation?.additionalProperties || {};
    if (this.selectedPlaceDetails && businessInformation?.placeId && this.selectedPlaceDetails.placeId !== businessInformation.placeId) {
      this.selectedPlaceDetails = null;
    }
    const primaryPhoneNumber = business?.phoneNumbers?.find(p => p.type === BusinessPhoneNumberType.Main);
    const notificationPhoneNumber = businessInformation?.notifications?.filter((notification) => !!notification.destination && notification.type === NotificationChannelType.Sms)?.[0]?.destination || '';
    this.services = business?.services?.map((service) => service.name) || [];
    this.sourceEditModeEnabled = !this.hasGoogleBusinessProfile && !this.hasBusinessUrl && !this.isTrainingInProgress;

    const initialBackgroundNoise = (this.agent.primary?.configuration?.backgroundNoise || BackgroundNoiseType.Office).toLowerCase();
    this.setupForm = this.fb.group({
      title: [business?.title || '', Validators.required],
      name: [businessInformation?.name || business?.title || ''],
      summary: [businessInformation?.summary || '', [Validators.required, Validators.maxLength(500)]],
      primaryBusinessPhoneNumber: this.fb.group({ isActive: !!primaryPhoneNumber, number: [formatPhoneNumber(primaryPhoneNumber?.number), Validators.required], type: BusinessPhoneNumberType.Main, id: primaryPhoneNumber?.id, isPrimary: true, countryPrefix: primaryPhoneNumber?.countryPrefix || 1 }),
      emailNotificationEnabled: this.agent.primary?.configuration?.callFinishedNotificationChannel?.includes(CallFinishedNotificationChannel.Email) || false,
      smsNotificationEnabled: this.agent.primary?.configuration?.callFinishedNotificationChannel?.includes(CallFinishedNotificationChannel.SMS) || false,
      url: [businessInformation?.url || '', Validators.pattern(WEBSITE_URL_PATTERN)],
      placeId: businessInformation?.placeId || '',
      questions: this.config.createCustomQuestions(this.agent.customQuestions || [], true),
      scenarios: this.config.createScenarios(
        (this.agent.messageScenarios || []).map(s => ({ ...s, suggested: !this.isCustomizeStepCompleted })),
      ),
      faqs: this.config.createFaqs(this.agent.faqs || []),
      agentProfile: this.fb.group({
        title: [this.agent.primary?.title || 'Rosie', Validators.required],
        voiceId: [this.agent.primary?.configuration?.voiceId || 'rosie', Validators.required],
        backgroundNoise: [initialBackgroundNoise, Validators.required],
        voiceSpeed: [this.agent.primary?.configuration?.voiceSpeed || 'average', Validators.required],
      }),
      customGreeting: this.fb.group({
        hasCustomGreetingEnabled: [this.agent.primary?.configuration?.hasCustomGreetingEnabled || false],
        message: [this.getCustomGreetingMessage(), [Validators.maxLength(250)]],
        hasLegalDisclaimerEnabled: [this.agent.primary?.configuration?.hasLegalDisclaimerEnabled !== undefined ? this.agent.primary?.configuration?.hasLegalDisclaimerEnabled : true],
        legalDisclaimer: [this.agent.primary?.greeting?.legalDisclaimer || 'Our call may be recorded today for quality control purposes.']
      }),
      premiumFeatures: this.fb.group({
        appointments: this.fb.group({
          hasAppointmentsEnabled: [this.agent.primary?.configuration?.hasAppointmentsEnabled || false],
          message: [this.agent.primary?.bookingMessage?.message || `${business?.title || ''}: Schedule an appointment with the following link`, [Validators.required, Validators.maxLength(300)]],
          url: [this.agent.primary?.bookingMessage?.url || '', [Validators.required, Validators.maxLength(250)]]
        }),
        textLinks: this.fb.group({
          hasTextLinksEnabled: [this.agent.primary?.configuration?.hasTextLinkEnabled || false],
          formArray: this.config.createTextLinks(business?.textALinkMessages || [])
        }),
        transferCalls: this.fb.group({
          hasTransferCallsEnabled: [this.agent.primary?.configuration?.hasTransferCallsEnabled || false],
          formArray: this.config.createTransferDepartments(business?.transferDepartments || [])
        }),
        scheduling: this.fb.group({
          hasAppointmentSchedulingEnabled: [this.agent.primary?.configuration?.hasAppointmentSchedulingEnabled || false],
          calendarId: [this.agent.primary?.calendar?.config?.calendarId || null],
          meetingTitle: [this.agent.primary?.calendar?.config?.meetingTitle || 'New Appointment', Validators.required],
          meetingDuration: [this.agent.primary?.calendar?.config?.meetingDuration || 60, Validators.required],
          meetingEventType: [this.agent.primary?.calendar?.config?.meetingEventType || this.agent.primary?.calendar?.details?.eventTypes?.[0]?.uri || null],
          bookingQuestions: this.config.createCustomQuestions((this.agent.customQuestions || []).filter(q => (q as any).type === CustomQuestionType.CalendarEvent), false),
          attendeeEmails: this.config.createAttendeeEmails(this.config.resolveAttendeeEmails(this.agent.primary))
        })
      }),
      notificationPhoneNumber: [notificationPhoneNumber || ''],
      websiteTextingPreSelected: [this.agent.primary?.configuration?.websiteTextingPreSelected || false],
    });

    this.initialSetupFormValue = this.setupForm.value;
  }

  refreshInitialState(retryCount = 0, maxRetries = 20) {
    if (this.session.isAwaitingInitialSetup && retryCount < maxRetries) {
      this.user.getUser().subscribe((user) => {
        if (this.user.userData?.subscription) {
          this.session.clearAwaitingInitialSetup();
        } else {
          setTimeout(() => this.refreshInitialState(retryCount + 1, maxRetries), 1500);
        }
      });
    } else if (retryCount >= maxRetries) {
      this.session.clearAwaitingInitialSetup();
    }
  }

  private refreshFormData() {
    forkJoin([this.agent.getCustomQuestions(), this.agent.getFaqs(), this.agent.getMessageScenarios().pipe(catchError(() => of([])))]).subscribe(() => this.resetSetupForm());
  }

  private resetSetupForm() {
    const business = this.agent.business;
    const businessInformation = this.agent.businessInformation?.additionalProperties || {};
    const primaryPhoneNumber = business?.phoneNumbers?.find(p => p.type === BusinessPhoneNumberType.Main);

    this.services = business?.services?.map((service) => service.name) || [];
    const value = {
      title: business?.title,
      name: businessInformation?.name || business?.title,
      summary: businessInformation?.summary,
      url: businessInformation.url,
      placeId: businessInformation.placeId,
      emailNotificationEnabled: this.agent.primary?.configuration?.callFinishedNotificationChannel?.includes(CallFinishedNotificationChannel.Email) || false,
      smsNotificationEnabled: this.agent.primary?.configuration?.callFinishedNotificationChannel?.includes(CallFinishedNotificationChannel.SMS) || false,
      primaryBusinessPhoneNumber: {
        isActive: true,
        number: formatPhoneNumber(primaryPhoneNumber?.number),
        type: BusinessPhoneNumberType.Main,
        id: primaryPhoneNumber?.id,
        isPrimary: true,
        countryPrefix: primaryPhoneNumber?.countryPrefix || 1
      }
    };
    this.setupForm.patchValue(value, { emitEvent: false });

    const questionsFormArray = this.setupForm.get('questions') as FormArray;
    if (questionsFormArray) {
      questionsFormArray.clear();
      const newQuestions = this.agent.customQuestions || [];
      newQuestions.forEach(item => questionsFormArray.push(this.config.createCustomQuestion(item, false)));
    }

    const scenariosFormArray = this.setupForm.get('scenarios') as FormArray;
    if (scenariosFormArray) {
      scenariosFormArray.clear();
      const newScenarios = this.agent.messageScenarios || [];
      const markSuggested = !this.isCustomizeStepCompleted;
      newScenarios.forEach(item => scenariosFormArray.push(
        this.config.createScenario({ ...item, suggested: markSuggested }),
      ));
    }

    const faqsFormArray = this.setupForm.get('faqs') as FormArray;
    if (faqsFormArray) {
      faqsFormArray.clear();
      const newFaqs = this.agent.faqs || [];
      newFaqs.forEach(item => faqsFormArray.push(this.config.createFaq(item)));
    }

    const agentProfileForm = this.setupForm.get('agentProfile');
    if (agentProfileForm) {
      const currentBackgroundNoise = (this.agent.primary?.configuration?.backgroundNoise || BackgroundNoiseType.Office).toLowerCase();
      agentProfileForm.patchValue({
        title: this.agent.primary?.title || 'Rosie',
        voiceId: this.agent.primary?.configuration?.voiceId || 'rosie',
        backgroundNoise: currentBackgroundNoise,
        voiceSpeed: this.agent.primary?.configuration?.voiceSpeed || 'average'
      }, { emitEvent: false });
    }

    const customGreetingForm = this.setupForm.get('customGreeting');
    if (customGreetingForm) {
      customGreetingForm.patchValue({
        hasCustomGreetingEnabled: this.agent.primary?.configuration?.hasCustomGreetingEnabled || false,
        message: this.getCustomGreetingMessage(),
        hasLegalDisclaimerEnabled: this.agent.primary?.configuration?.hasLegalDisclaimerEnabled !== undefined ? this.agent.primary?.configuration?.hasLegalDisclaimerEnabled : true,
        legalDisclaimer: this.agent.primary?.greeting?.legalDisclaimer || 'Our call may be recorded today for quality control purposes.'
      }, { emitEvent: false });
    }

    const premiumFeaturesForm = this.setupForm.get('premiumFeatures');
    if (premiumFeaturesForm) {
      const appointmentsForm = premiumFeaturesForm.get('appointments');
      if (appointmentsForm) {
        appointmentsForm.patchValue({
          hasAppointmentsEnabled: this.agent.primary?.configuration?.hasAppointmentsEnabled || false,
          message: this.agent.primary?.bookingMessage?.message || `Thanks for calling ${business?.title || ''}! Use the link below to schedule an appointment with us.`,
          url: this.agent.primary?.bookingMessage?.url || ''
        }, { emitEvent: false });

        // Ensure required validators are set
        appointmentsForm.get('message')?.setValidators([Validators.required, Validators.maxLength(300)]);
        appointmentsForm.get('url')?.setValidators([Validators.required, Validators.maxLength(250)]);
        appointmentsForm.get('message')?.updateValueAndValidity();
        appointmentsForm.get('url')?.updateValueAndValidity();
      }

      const textLinksForm = premiumFeaturesForm.get('textLinks');
      if (textLinksForm) {
        textLinksForm.get('hasTextLinksEnabled')?.setValue(this.agent.primary?.configuration?.hasTextLinkEnabled || false, { emitEvent: false });

        const textLinksArray = textLinksForm.get('formArray') as FormArray;
        if (textLinksArray) {
          textLinksArray.clear();
          if (business?.textALinkMessages?.length > 0) {
            business.textALinkMessages.forEach(item => textLinksArray.push(this.config.createTextLink(item, business?.title)));
          }
        }
      }

      const transferCallsForm = premiumFeaturesForm.get('transferCalls');
      if (transferCallsForm) {
        transferCallsForm.get('hasTransferCallsEnabled')?.setValue(this.agent.primary?.configuration?.hasTransferCallsEnabled || false, { emitEvent: false });

        const transferCallsArray = transferCallsForm.get('formArray') as FormArray;
        if (transferCallsArray) {
          transferCallsArray.clear();
          if (business?.transferDepartments?.length > 0) {
            business.transferDepartments.forEach(item => transferCallsArray.push(this.config.createTransferDepartment(item)));
          }
        }
      }
    }

    const notificationPhoneNumber = businessInformation?.notifications?.filter((notification) => !!notification.destination && notification.type === NotificationChannelType.Sms)?.[0]?.destination || '';
    if (this.setupForm.get('notificationPhoneNumber')) {
      this.setupForm.get('notificationPhoneNumber').setValue(notificationPhoneNumber, { emitEvent: false });
    }

    if (this.setupForm.get('websiteTextingPreSelected')) {
      this.setupForm.get('websiteTextingPreSelected').setValue(this.agent.primary?.configuration?.websiteTextingPreSelected || false, { emitEvent: false });
    }

    this.initialSetupFormValue = this.setupForm.value;

  }

  updateBusinessServices(): Observable<any> {
    const business = this.agent.business;
    if (!business) {
      return of(null);
    }

    const hasChanges = !areEqual(this.services, business.services?.map((service) => service.name));

    if (!hasChanges) {
      return of(null);
    }

    const services = this.services.map(service => ({
      name: service,
      isActive: true,
      id: this.getBusinessService(service)?.id || null
    })) || [];;

    return this.businessService.updateServices(services);
  }

  private getBusinessService(serviceName: string) {
    const business = this.agent.business;
    return business?.services?.find((service) => service.name === serviceName);
  }

  updateBusinessInformation(): Observable<any> {
    const notificationPhoneNumberControl = this.setupForm.get('notificationPhoneNumber');
    const notifications = this.agent.businessInformation?.additionalProperties?.notifications || [];
    const isPhoneNumberValid = notificationPhoneNumberControl.value && notificationPhoneNumberControl.valid;
    const businessInfo = this.setupForm.value;
    if (isPhoneNumberValid && !notifications.find((notification) => notification.destination === notificationPhoneNumberControl.value)) {
      const callTypes = {
        [ConversationType.Appointment]: true,
        [ConversationType.Message]: true,
        [ConversationType.Transferred]: false,
        [ConversationType.Spam]: false,
        [ConversationType.HangUp]: false,
      };
      notifications.push({ destination: notificationPhoneNumberControl.value, type: NotificationChannelType.Sms, configuration: { callTypes } });
    }

    const businessInformationData: any = {
      additionalProperties: {
        summary: businessInfo.summary,
        notifications
      }
    };
    return this.businessService.updateInformation(businessInformationData);
  }

  updateBusiness(): Observable<any> {
    const businessForm = this.setupForm.value;
    const title = businessForm.title;
    const primaryPhoneNumber = businessForm.primaryBusinessPhoneNumber;

    const updates = [];

    updates.push(this.businessService.update({ title }));

    if (primaryPhoneNumber && primaryPhoneNumber.number) {
      const initialPhoneNumber = this.initialSetupFormValue?.primaryBusinessPhoneNumber?.number;
      const hasPhoneNumberChanged = primaryPhoneNumber.number !== initialPhoneNumber;

      if (hasPhoneNumberChanged) {
        const phoneNumber = primaryPhoneNumber;
        updates.push(this.businessService.updatePhoneNumber(phoneNumber));
      }
    }

    return updates.length > 1 ? forkJoin(updates) : updates[0];
  }

  updateCustomQuestions(): void {
    const customQuestions = this.setupForm.get('questions').value?.filter((question) => !!question.question);
    const questions = customQuestions.map((question, index) => ({ question: question.question, priority: index, type: CustomQuestionType.Note }));
    this.agent.updateCustomQuestions(questions).subscribe();
  }

  updateScenarios(): void {
    const scenariosValue = (this.setupForm.get('scenarios')?.value || []) as any[];
    const scenarios = scenariosValue
      .filter((s) => s?.title && s?.text)
      .map((s, index) => ({
        id: s.id || undefined,
        title: s.title,
        text: s.text,
        priority: s.priority ?? index
      }));
    this.agent.saveMessageScenarios(scenarios).subscribe();
  }

  updateFaqs(): Observable<any> {
    const faqs = this.setupForm.get('faqs').value?.filter((faq) => !!faq.question && !!faq.answer);
    return this.agent.updateFaqs(faqs);
  }

  updateAgentProfile(): Observable<any> {
    const profileForm = this.setupForm.get('agentProfile').value;
    const updates = [];

    if (profileForm.title !== this.initialSetupFormValue?.agentProfile?.title) {
      const agentData = {
        title: profileForm.title,
        id: this.agent.primary.id,
        businessId: this.agent.primary.businessId,
        sms: this.agent.primary.sms
      };
      updates.push(this.agent.updateAgent(agentData));
    }

    const configChanges: any = {};
    if (profileForm.backgroundNoise !== this.initialSetupFormValue?.agentProfile?.backgroundNoise) {
      configChanges.backgroundNoise = profileForm.backgroundNoise;
    }
    if (profileForm.voiceId !== this.initialSetupFormValue?.agentProfile?.voiceId) {
      configChanges.voiceId = profileForm.voiceId;
    }
    if (profileForm.voiceSpeed !== this.initialSetupFormValue?.agentProfile?.voiceSpeed) {
      configChanges.voiceSpeed = profileForm.voiceSpeed;
    }
    if (Object.keys(configChanges).length > 0) {
      updates.push(this.agent.updateAgentConfiguration({ configuration: configChanges }));
    }
    return updates.length > 0 ? forkJoin(updates) : of(null);
  }

  updateCustomGreeting(): Observable<any> {
    const greetingForm = this.setupForm.get('customGreeting').value;
    const configData = {
      configuration: {
        hasCustomGreetingEnabled: true,
        customGreeting: greetingForm.message,
        hasLegalDisclaimerEnabled: greetingForm.hasLegalDisclaimerEnabled
      }
    };

    return this.agent.updateAgentConfiguration(configData);
  }

  updateAppointments(): Observable<any> {
    const appointmentsForm = this.setupForm.get('premiumFeatures')?.get('appointments')?.getRawValue();
    if (!appointmentsForm) return of(null);

    const updates = [];


    const appointmentsConfig = {
      configuration: {
        hasAppointmentsEnabled: appointmentsForm.hasAppointmentsEnabled
      }
    };

    updates.push(this.agent.updateAgentConfiguration(appointmentsConfig));

    if (appointmentsForm.hasAppointmentsEnabled && (appointmentsForm.message || appointmentsForm.url)) {
      const bookingMessage = {
        message: appointmentsForm.message,
        url: appointmentsForm.url,
        id: this.agent.primary?.bookingMessage?.id || null
      };
      updates.push(this.agent.updateBookingMessage(bookingMessage));
    }

    return forkJoin(updates);
  }

  updateScheduling(): Observable<any> {
    const scheduling = this.setupForm.get('premiumFeatures')?.get('scheduling')?.getRawValue();
    if (!scheduling) return of(null);

    const updates = [];

    const schedulingConfig = {
      configuration: {
        hasAppointmentSchedulingEnabled: scheduling.hasAppointmentSchedulingEnabled,
        calendarConfig: {
          meetingTitle: scheduling.meetingTitle,
          meetingDuration: scheduling.meetingDuration,
          meetingEventType: scheduling.meetingEventType,
          calendarId: scheduling.calendarId,
          attendeeEmails: scheduling.attendeeEmails
        }
      }
    } as any;

    updates.push(this.agent.updateAgentConfiguration(schedulingConfig));

    if (scheduling.hasAppointmentSchedulingEnabled) {
      const existingQuestions = this.agent.customQuestions || [];
      const otherTypeQuestions = existingQuestions.filter((q: any) => q.type !== CustomQuestionType.CalendarEvent);

      const calendarEventQuestions = (scheduling.bookingQuestions || []).map((question, index) => {
        const existingQuestion = existingQuestions.find((q: any) =>
          q.type === CustomQuestionType.CalendarEvent && q.question === question.question
        );
        return {
          id: existingQuestion?.id,
          question: question.question,
          priority: otherTypeQuestions.length + index,
          type: CustomQuestionType.CalendarEvent
        };
      });

      const allQuestions = [
        ...otherTypeQuestions.map((q: any, index) => ({
          id: q.id,
          question: q.question,
          priority: index,
          type: q.type
        })),
        ...calendarEventQuestions
      ].filter(q => q.question && q.question.trim() !== '');

      updates.push(this.agent.updateCustomQuestions(allQuestions));
    }

    return forkJoin(updates);
  }

  updateTextLinks(): Observable<any> {
    const textLinksForm = this.setupForm.get('premiumFeatures')?.get('textLinks')?.value;
    if (!textLinksForm) return of(null);

    const updates = [];
    const formArrayData = textLinksForm.formArray || [];

    const textLinksConfig = {
      configuration: {
        hasTextLinkEnabled: textLinksForm.hasTextLinksEnabled
      }
    };
    updates.push(this.agent.updateAgentConfiguration(textLinksConfig));

    if (textLinksForm.hasTextLinksEnabled && formArrayData.length > 0) {
      const validTextLinks = formArrayData.filter(item => item.message && item.url && item.department);
      if (validTextLinks.length > 0) {
        updates.push(this.businessService.updateTextLinks(validTextLinks));
      }
    }

    return updates.length > 0 ? forkJoin(updates) : of(null);
  }

  updateTransferCalls(): Observable<any> {
    const transferCallsForm = this.setupForm.get('premiumFeatures')?.get('transferCalls')?.value;
    if (!transferCallsForm) return of(null);

    const updates = [];
    const formArrayData = transferCallsForm.formArray || [];

    const transferConfig = {
      configuration: {
        hasTransferCallsEnabled: transferCallsForm.hasTransferCallsEnabled
      }
    };
    updates.push(this.agent.updateAgentConfiguration(transferConfig));

    if (transferCallsForm.hasTransferCallsEnabled && formArrayData.length > 0) {
      const validTransferCalls = formArrayData.filter(item =>
        item.department && item.destinations?.some(dest => dest.number)
      );
      if (validTransferCalls.length > 0) {
        updates.push(this.businessService.updateTransferDepartments(validTransferCalls));
      }
    }
    return updates.length > 0 ? forkJoin(updates) : of(null);
  }

  // Persists the unified Customize answers (ENG-2292). Always sends all four
  // keys; the API merges them into business.meta. The user is re-read so the
  // Go Live step sees the refreshed intent and recommended plan.
  updateOnboardingIntent(intent: OnboardingIntent): Observable<any> {
    // The intent is sent with snake_case keys on purpose: the HTTP interceptor's
    // camelCase -> snake_case pass turns `textALink` into `text_alink` (its regex
    // only splits a lowercase/uppercase pair), which the API rejects as an
    // unknown key. Keys that are already snake_case pass through unchanged.
    const payload = {
      book_appointments: intent.bookAppointments ?? null,
      booking_mode: intent.bookingMode ?? null,
      transfer_calls: intent.transferCalls ?? null,
      text_a_link: intent.textALink ?? null,
    };
    return this.businessService.update({ title: this.business?.title, meta: { onboarding_intent: payload } }).pipe(
      switchMap(() => this.user.getUser()),
    );
  }

  updateNotifications(setupFormValue) {
    const emailNotificationEnabled = setupFormValue?.emailNotificationEnabled;
    const smsNotificationEnabled = setupFormValue?.smsNotificationEnabled;
    const callFinishedNotificationChannel = [];
    if (emailNotificationEnabled && !callFinishedNotificationChannel?.includes(CallFinishedNotificationChannel.Email)) {
      callFinishedNotificationChannel.push(CallFinishedNotificationChannel.Email);
    }
    if (smsNotificationEnabled && !callFinishedNotificationChannel?.includes(CallFinishedNotificationChannel.SMS)) {
      callFinishedNotificationChannel.push(CallFinishedNotificationChannel.SMS);
    }
    this.agent.updateAgentConfiguration({ configuration: { callFinishedNotificationChannel } }).subscribe();
  }

  createTextLinkForm(textLink?: any) {
    return this.config.createTextLink(textLink, this.business?.title);
  }

  createTransferCallForm(transferCall?: any) {
    return this.config.createTransferDepartment(transferCall);
  }

  createFaqForm(faq?: any) {
    return this.config.createFaq(faq);
  }

  createCustomQuestionForm(question?: any, isRequired: boolean = false) {
    return this.config.createCustomQuestion(question, isRequired);
  }
}


export enum GuidedSetupStepType {
  Train = 'train',
  Customize = 'customize',
  Test = 'test',
  Launch = 'launch',
}

export interface GuidedSetupStep {
  number: number;
  label: string;
  key: GuidedSetupStepType;
  completionFlag?: keyof Configuration;
}

// Sub-screens of the unified Customize step (ENG-2292), in flow order. The
// key is also what gets persisted to lastActiveConfigurationStep for resume.
export enum CustomizeScreen {
  AgentName = 'agentName',
  AgentVoice = 'agentVoice',
  Appointments = 'appointments',
  Transfers = 'transfers',
  TextLink = 'textALink',
}

export const CUSTOMIZE_SCREEN_ORDER: CustomizeScreen[] = [
  CustomizeScreen.AgentName,
  CustomizeScreen.AgentVoice,
  CustomizeScreen.Appointments,
  CustomizeScreen.Transfers,
  CustomizeScreen.TextLink,
];